import '../styles/NewTestRunConfigPanel.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { projectData } from '../lib/projectData';

const LOCAL_SETTINGS_KEY = 'brute.newRun.settings';

const FORMATS = [
  { id: 'wordlist', label: 'Wordlist', code: 'wordlist.txt' },
  { id: 'mask', label: 'Masked input', code: '?u?l?d?s' },
  { id: 'custom', label: 'Custom list', code: 'one per line' },
];

const POLICIES = [
  { id: 'default', label: 'Default policy' },
  { id: 'strict', label: 'Strict policy' },
  { id: 'permissive', label: 'Permissive policy' },
];

const DEFAULTS = { threadCount: '4', policy: 'default', inputFormat: 'wordlist', target: '', runName: '' };

const FIELDS = [
  { key: 'target', label: 'Target identification', fieldId: 'ntrc-target' },
  { key: 'inputFormat', label: 'Input format', fieldId: 'ntrc-input-format' },
  { key: 'threadCount', label: 'Thread count', fieldId: 'ntrc-threads' },
  { key: 'policy', label: 'Policy', fieldId: 'ntrc-policy' },
  { key: 'runName', label: 'Run name', fieldId: 'ntrc-run-name' },
];
const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f]));

function readLocalDefaults() {
  return new Promise((resolve, reject) => {
    window.setTimeout(() => {
      try {
        const raw = window.localStorage ? window.localStorage.getItem(LOCAL_SETTINGS_KEY) : null;
        const stored = raw ? JSON.parse(raw) : null;
        if (!stored || typeof stored !== 'object') {
          resolve({ ...DEFAULTS });
          return;
        }
        resolve({
          ...DEFAULTS,
          threadCount: String(stored.threadCount ?? DEFAULTS.threadCount),
          policy: POLICIES.some((p) => p.id === stored.policy) ? stored.policy : DEFAULTS.policy,
          inputFormat: FORMATS.some((f) => f.id === stored.inputFormat) ? stored.inputFormat : DEFAULTS.inputFormat,
        });
      } catch (err) {
        reject(err);
      }
    }, 320);
  });
}

function summaryFor(form) {
  const format = FORMATS.find((f) => f.id === form.inputFormat);
  const policy = POLICIES.find((p) => p.id === form.policy);
  return `${format ? format.label : form.inputFormat} · ${form.threadCount || '—'} threads · ${policy ? policy.label.toLowerCase() : form.policy}`;
}

function parseThreads(value) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 64 ? n : null;
}

function validate(form) {
  const errors = {};
  const target = form.target.trim();
  if (!target) errors.target = 'Enter the authorized target host or label.';
  else if (target.length > 120) errors.target = 'Keep the target under 120 characters.';
  else if (!/^[A-Za-z0-9](?:[A-Za-z0-9._:-]*[A-Za-z0-9])?$/.test(target)) errors.target = 'Use letters, digits, dots, hyphens, underscores or colons only.';
  if (!FORMATS.some((f) => f.id === form.inputFormat)) errors.inputFormat = 'Choose an input format: wordlist, masked input or custom list.';
  if (parseThreads(form.threadCount) === null) errors.threadCount = 'Thread count must be a whole number from 1 to 64.';
  if (!POLICIES.some((p) => p.id === form.policy)) errors.policy = 'Choose a policy for this run.';
  if (!form.runName.trim()) errors.runName = 'Give this run a name so it can be found in History.';
  else if (form.runName.trim().length > 80) errors.runName = 'Keep the run name under 80 characters.';
  return errors;
}

function monoStamp(iso) {
  return iso ? iso.slice(0, 16).replace('T', ' ') : '—';
}

export default function NewTestRunConfigPanel() {
  const [form, setForm] = useState({ ...DEFAULTS });
  const [baseline, setBaseline] = useState({ ...DEFAULTS });
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationSummary, setValidationSummary] = useState(null);
  const [submitStatus, setSubmitStatus] = useState({ kind: 'idle' });
  const [loadState, setLoadState] = useState({ kind: 'loading' });
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [prefill, setPrefill] = useState({ kind: 'none' });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    (async () => {
      try {
        const defaults = await readLocalDefaults();
        if (cancelled) return;
        const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
        const runId = params.get('id');
        if (runId) {
          const records = await projectData.list('test_run');
          if (cancelled) return;
          const record = (records || []).find((r) => r.id === runId);
          if (record) {
            const next = {
              target: record.target || '',
              inputFormat: defaults.inputFormat,
              threadCount: defaults.threadCount,
              policy: defaults.policy,
              runName: record.name || '',
            };
            setForm(next);
            setBaseline(next);
            setPrefill({ kind: 'loaded', record });
          } else {
            setForm(defaults);
            setBaseline(defaults);
            setPrefill({ kind: 'missing', id: runId });
          }
        } else {
          setForm(defaults);
          setBaseline(defaults);
          setPrefill({ kind: 'none' });
        }
        setDefaultsLoaded(true);
        setLoadState({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        setLoadState({ kind: 'error', message: (err && err.message) || 'Defaults could not be read from local settings.' });
      }
    })();
    return () => { cancelled = true; mounted.current = false; };
  }, []);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);
  const busy = submitStatus.kind === 'busy';
  const locked = loadState.kind !== 'ready' || busy;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSubmitStatus({ kind: 'idle' });
  };

  const blurField = (key) => {
    const errors = validate(form);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (errors[key]) next[key] = errors[key];
      else delete next[key];
      return next;
    });
  };

  const focusField = (key) => {
    const el = document.getElementById(FIELD_BY_KEY[key].fieldId);
    if (el) el.focus();
  };

  const started = submitStatus.kind === 'started';

  const handleStart = async (event) => {
    event.preventDefault();
    if (busy || loadState.kind !== 'ready') return;
    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setValidationSummary({ keys: Object.keys(errors) });
      setSubmitStatus({ kind: 'idle' });
      return;
    }
    setValidationSummary(null);
    setSubmitStatus({ kind: 'busy' });
    const now = new Date();
    const iso = now.toISOString().slice(0, 19);
    try {
      const created = await projectData.create('test_run', {
        name: form.runName.trim(),
        target: form.target.trim(),
        configurationSummary: summaryFor(form),
        state: 'queued',
        createdAt: iso,
        updatedAt: iso,
      });
      if (!created || !created.id) throw new Error('The run was created without an identifier.');
      const params = new URLSearchParams();
      params.set('id', created.id);
      params.set('target', created.target || form.target.trim());
      params.set('inputFormat', form.inputFormat);
      params.set('threads', form.threadCount);
      params.set('policy', form.policy);
      params.set('return', '/new-test-run');
      const href = `/run-monitor?${params.toString()}`;
      setSubmitStatus({ kind: 'started', id: created.id, href });
      window.location.assign(href);
    } catch (err) {
      setSubmitStatus({ kind: 'error', message: (err && err.message) || 'The run could not be created.' });
    }
  };

  const handleSaveDraft = () => {
    if (locked) return;
    setSubmitStatus({ kind: 'saving' });
    try {
      const threads = parseThreads(form.threadCount);
      const payload = {
        target: form.target.trim(),
        threadCount: threads || DEFAULTS.threadCount,
        policy: form.policy,
        inputFormat: form.inputFormat,
        runName: form.runName,
      };
      window.localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(payload));
      const stamp = new Date().toISOString();
      setBaseline({ ...form });
      setDraftSavedAt(stamp);
      setSubmitStatus({ kind: 'saved', at: stamp });
    } catch (err) {
      setSubmitStatus({ kind: 'error', message: 'The draft could not be saved to local storage.' });
    }
  };

  const retryLoad = () => {
    setLoadState({ kind: 'loading' });
    setDefaultsLoaded(false);
    readLocalDefaults()
      .then((defaults) => {
        if (!mounted.current) return;
        setForm(defaults);
        setBaseline(defaults);
        setDefaultsLoaded(true);
        setLoadState({ kind: 'ready' });
      })
      .catch((err) => {
        if (!mounted.current) return;
        setLoadState({ kind: 'error', message: (err && err.message) || 'Defaults could not be read from local settings.' });
      });
  };

  const summaryKeys = validationSummary ? validationSummary.keys : [];

  const renderField = (key, wide, control) => {
    const meta = FIELD_BY_KEY[key];
    const err = fieldErrors[key];
    return (
      <div
        key={key}
        id={`ntrc-field-${key}`}
        className={`ntrc-field${wide ? ' ntrc-field--wide' : ''}${err ? ' ntrc-field--invalid' : ''}`}
        data-field={key}
      >
        <label className="ntrc-label" htmlFor={meta.fieldId}>
          <span>{meta.label}</span>
        </label>
        {control}
        {err && <p className="ntrc-msg" id={`${meta.fieldId}-error`} role="alert">{err}</p>}
      </div>
    );
  };

  return (
    <section className="ntrc-root" aria-labelledby="ntrc-panel-title" data-content-rail="workspace">
      <div className="ntrc-rail">
        <form className={`ntrc-panel${busy ? ' ntrc-panel--busy' : ''}`} onSubmit={handleStart} noValidate aria-busy={busy}>
          <div className="ntrc-panel__head">
            <h2 className="ntrc-panel__title" id="ntrc-panel-title">Run configuration</h2>
            <span className="ntrc-chip">
              <span className="ntrc-chip__dot" aria-hidden="true" />
              Authorized target only
            </span>
          </div>

          {loadState.kind === 'loading' && (
            <div className="ntrc-skel" role="status" aria-live="polite">
              <span className="ntrc-skel__note">Loading defaults from local settings…</span>
              <div className="ntrc-skel__grid" aria-hidden="true">
                <div className="ntrc-skel__block ntrc-skel__block--wide">
                  <span className="ntrc-skel__bar ntrc-skel__bar--label" />
                  <span className="ntrc-skel__bar ntrc-skel__bar--field" />
                </div>
                <div className="ntrc-skel__block ntrc-skel__block--wide">
                  <span className="ntrc-skel__bar ntrc-skel__bar--label" />
                  <span className="ntrc-skel__bar ntrc-skel__bar--field" />
                </div>
                {['Threads', 'Policy', 'Run name'].map((label) => (
                  <div className="ntrc-skel__block" key={label}>
                    <span className="ntrc-skel__bar ntrc-skel__bar--label" />
                    <span className="ntrc-skel__bar ntrc-skel__bar--field" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {loadState.kind === 'error' && (
            <div className="ntrc-error" role="alert">
              <p className="ntrc-error__title">Defaults unavailable</p>
              <p className="ntrc-error__msg">{loadState.message}</p>
              <p className="ntrc-summary__note">The form can&rsquo;t open until local settings are readable.</p>
              <button type="button" className="ntrc-btn ntrc-btn--outline" onClick={retryLoad}>Retry reading settings</button>
            </div>
          )}

          {loadState.kind === 'ready' && defaultsLoaded && (
            <>
              {prefill.kind === 'loaded' && (
                <p className="ntrc-notice ntrc-notice--info">
                  <span className="ntrc-notice__label">Prefilled</span>
                  <span>
                    Configuration preserved from <span className="ntrc-mono">{prefill.record.name}</span> ({prefill.record.id}). Correct anything below and resubmit.
                  </span>
                </p>
              )}
              {prefill.kind === 'missing' && (
                <p className="ntrc-notice ntrc-notice--error" role="status">
                  <span className="ntrc-notice__label">No run</span>
                  <span>No saved run matches id <span className="ntrc-mono">{prefill.id}</span>. The fields start from your local defaults.</span>
                </p>
              )}

              {summaryKeys.length > 0 && (
                <div className="ntrc-summary" role="alert" aria-labelledby="ntrc-summary-title">
                  <div className="ntrc-summary__head">
                    <span className="ntrc-summary__tag">{summaryKeys.length} {summaryKeys.length === 1 ? 'issue' : 'issues'}</span>
                    <h3 className="ntrc-summary__title" id="ntrc-summary-title">Fix these before starting</h3>
                  </div>
                  <ul className="ntrc-summary__list">
                    {summaryKeys.map((key) => (
                      <li key={key}>
                        <button type="button" className="ntrc-summary__link" onClick={() => focusField(key)}>
                          <span className="ntrc-summary__icon" aria-hidden="true">!</span>
                          {FIELD_BY_KEY[key].label}: {fieldErrors[key]}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="ntrc-summary__note">No run started — nothing was saved or executed.</p>
                </div>
              )}

              <div className="ntrc-grid">
                {renderField('target', true, (
                  <>
                    <input
                      id="ntrc-target"
                      className="ntrc-input ntrc-mono"
                      type="text"
                      value={form.target}
                      onChange={(e) => setField('target', e.target.value)}
                      onBlur={() => blurField('target')}
                      disabled={busy}
                      placeholder="staging-auth.internal"
                      autoComplete="off"
                      spellCheck="false"
                      aria-invalid={Boolean(fieldErrors.target)}
                      aria-describedby={fieldErrors.target ? 'ntrc-target-error' : undefined}
                      maxLength={160}
                    />
                    <span className="ntrc-label__hint">Host or label of a system you are authorized to test</span>
                  </>
                ))}

                {renderField('inputFormat', true, (
                  <div className="ntrc-seg" id="ntrc-input-format" role="radiogroup" aria-label="Input format" tabIndex={-1}>
                    {FORMATS.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        role="radio"
                        aria-checked={form.inputFormat === f.id}
                        tabIndex={form.inputFormat === f.id ? 0 : -1}
                        className="ntrc-seg__opt"
                        onClick={() => { setField('inputFormat', f.id); }}
                        disabled={busy}
                      >
                        <span>{f.label}</span>
                        <span className="ntrc-seg__code">{f.code}</span>
                      </button>
                    ))}
                  </div>
                ))}

                {renderField('threadCount', false, (
                  <div className="ntrc-stepper">
                    <button
                      type="button"
                      className="ntrc-stepper__btn"
                      aria-label="Decrease thread count"
                      onClick={() => setField('threadCount', String(Math.max(1, (parseThreads(form.threadCount) || 1) - 1)))}
                      disabled={busy || (parseThreads(form.threadCount) || 1) <= 1}
                    >−</button>
                    <input
                      id="ntrc-threads"
                      className="ntrc-stepper__input"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="64"
                      step="1"
                      value={form.threadCount}
                      onChange={(e) => setField('threadCount', e.target.value)}
                      onBlur={() => blurField('threadCount')}
                      disabled={busy}
                      aria-invalid={Boolean(fieldErrors.threadCount)}
                      aria-describedby={fieldErrors.threadCount ? 'ntrc-threads-error' : 'ntrc-threads-hint'}
                    />
                    <button
                      type="button"
                      className="ntrc-stepper__btn"
                      aria-label="Increase thread count"
                      onClick={() => setField('threadCount', String(Math.min(64, (parseThreads(form.threadCount) || 0) + 1)))}
                      disabled={busy || (parseThreads(form.threadCount) || 0) >= 64}
                    >+</button>
                  </div>
                ))}

                {renderField('policy', false, (
                  <select
                    id="ntrc-policy"
                    className="ntrc-select"
                    value={form.policy}
                    onChange={(e) => setField('policy', e.target.value)}
                    onBlur={() => blurField('policy')}
                    disabled={busy}
                    aria-invalid={Boolean(fieldErrors.policy)}
                    aria-describedby={fieldErrors.policy ? 'ntrc-policy-error' : undefined}
                  >
                    {POLICIES.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                ))}

                {renderField('runName', true, (
                  <input
                    id="ntrc-run-name"
                    className="ntrc-input"
                    type="text"
                    value={form.runName}
                    onChange={(e) => setField('runName', e.target.value)}
                    onBlur={() => blurField('runName')}
                    disabled={busy}
                    placeholder="Q1 staging credential sweep"
                    aria-invalid={Boolean(fieldErrors.runName)}
                    aria-describedby={fieldErrors.runName ? 'ntrc-run-name-error' : undefined}
                    maxLength={100}
                  />
                ))}
              </div>

              {submitStatus.kind === 'busy' && (
                <p className="ntrc-notice ntrc-notice--busy" role="status">
                  <span className="ntrc-notice__label">Starting</span>
                  <span>Creating the run record…</span>
                </p>
              )}
              {submitStatus.kind === 'started' && (
                <p className="ntrc-notice ntrc-notice--ok" role="status">
                  <span className="ntrc-notice__label">Queued</span>
                  <span>
                    Run <span className="ntrc-mono">{submitStatus.id}</span> created.{' '}
                    <a href={submitStatus.href}>Open Run Monitor →</a>
                  </span>
                </p>
              )}
              {submitStatus.kind === 'error' && (
                <p className="ntrc-notice ntrc-notice--error" role="alert">
                  <span className="ntrc-notice__label">Failed</span>
                  <span>{submitStatus.message} Your configuration is kept — resubmit when ready.</span>
                </p>
              )}
              {submitStatus.kind === 'saved' && (
                <p className="ntrc-notice ntrc-notice--ok" role="status">
                  <span className="ntrc-notice__label">Draft</span>
                  <span>Saved to local settings at <span className="ntrc-mono">{monoStamp(submitStatus.at)}</span>.</span>
                </p>
              )}
              {submitStatus.kind === 'saving' && (
                <p className="ntrc-notice" role="status">
                  <span className="ntrc-notice__label">Draft</span>
                  <span>Writing to local settings…</span>
                </p>
              )}

              <div className="ntrc-actions">
                {(submitStatus.kind === 'saved' || draftSavedAt) && submitStatus.kind === 'idle' && (
                  <span className="ntrc-actions__hint">Draft saved {monoStamp(draftSavedAt)}</span>
                )}
                <button type="submit" className="ntrc-btn ntrc-btn--primary ntrc-actions__primary" disabled={busy}>
                  {busy ? 'Starting…' : 'Start test run'}
                </button>
                <div className="ntrc-actions__secondary">
                  <button
                    type="button"
                    className="ntrc-btn ntrc-btn--outline"
                    onClick={handleSaveDraft}
                    disabled={busy || !dirty}
                  >
                    Save as draft
                  </button>
                  <a className="ntrc-btn ntrc-btn--text" href="/dashboard">Cancel</a>
                </div>
              </div>
            </>
          )}
        </form>

        <aside className="ntrc-aside" aria-label="Configuration summary">
          <p className="ntrc-aside__label">About to run</p>
          <p className="ntrc-aside__value">
            {parseThreads(form.threadCount) !== null ? `${form.threadCount} threads` : 'Threads: —'}
          </p>
          <p className="ntrc-aside__name">{form.target.trim() || 'Target not set yet'}</p>
          <ul className="ntrc-aside__list">
            <li className="ntrc-aside__row">
              <span className="ntrc-aside__key">Input</span>
              <span className="ntrc-aside__val">{(FORMATS.find((f) => f.id === form.inputFormat) || {}).label || '—'}</span>
            </li>
            <li className="ntrc-aside__row">
              <span className="ntrc-aside__key">Policy</span>
              <span className="ntrc-aside__val">{(POLICIES.find((p) => p.id === form.policy) || {}).label || '—'}</span>
            </li>
            <li className="ntrc-aside__row">
              <span className="ntrc-aside__key">Run name</span>
              <span className="ntrc-aside__val">{form.runName.trim() || '—'}</span>
            </li>
            <li className="ntrc-aside__row">
              <span className="ntrc-aside__key">Defaults</span>
              <span className="ntrc-aside__val">{loadState.kind === 'ready' ? 'local settings' : loadState.kind === 'loading' ? 'loading…' : 'unavailable'}</span>
            </li>
          </ul>
          <p className="ntrc-aside__foot">Settings, records and run history stay on this machine.</p>
          <span className="ntrc-aside__sticker">Authorized targets only</span>
        </aside>
      </div>
    </section>
  );
}